import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ethers } from "ethers";
import { useAccount, useConnect, useNetwork, useSwitchNetwork, useSignMessage } from "wagmi";
import { VR_RE_IMAGES } from "../../../data/vrRealEstateImages";
import { readOwnerOf, mintWithETH } from "../../../services/web3/osharRealEstateContract";
import { useAuth } from "../../../context/AuthContext";
import { trackCheckoutStarted } from "../../../services/analyticsClient";
import { getAuthToken } from "../../../services/authApi";
import {
  ensureTreydoraWalletSession,
  formatWalletSessionError,
  WALLET_LINK_HINT,
  WALLET_SIGN_IN_HINT,
  WALLET_SIGN_IN_SUBHINT,
} from "../../../lib/ensureTreydoraWalletSession";
import {
  clearPendingOsharWalletMintRecord,
  createOsharCheckoutSession,
  getOsharApiBaseUrl,
  prepareOsharWalletMint,
  readPendingOsharWalletMintRecord,
  retryPendingOsharWalletSync,
  syncOsharWalletMint,
  writePendingOsharWalletMintRecord,
} from "../../../services/osharRealEstateApi";
import { LEGAL_PATHS } from "../../../legal/legalPaths";
import {
  OSHAR_REAL_ESTATE_CHAIN_ID,
  OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
} from "../../../services/web3/contractConfig";
import { getChainLabel, getSwitchToNetworkLabel } from "../../../config/chainEnv";
import { isWrongNetwork } from "../../../services/web3/walletService";
import {
  resolveOsharRealEstateCardState,
  resolveOsharWalletButtonLabel,
  resolveOsharWalletStep,
  hasOsharWalletDebugFlags,
} from "../../../lib/osharRealEstateCardState";

const statusClass = (badgeLabel, cardStatus) => {
  if (badgeLabel === "Minted" || cardStatus === "sold_out") return "is-minted";
  if (badgeLabel === "Reserved" || cardStatus === "reserved") return "is-reserved";
  if (badgeLabel === "Unavailable" || cardStatus === "unavailable" || cardStatus === "not_configured") {
    return "is-unavailable";
  }
  if (badgeLabel === "Token Price Setup Pending" || cardStatus === "price_pending") return "is-unavailable";
  if (cardStatus === "loading") return "is-loading";
  if (cardStatus === "registry_offline") return "is-unavailable";
  return "is-available";
};

const shortenAddress = (value) => (value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "");

const resolveStatusLabel = ({ minted, statusOverride }) => {
  if (minted) return "Minted";
  if (statusOverride === "reserved") return "Reserved";
  if (statusOverride === "unavailable") return "Unavailable";
  return "Available";
};

function cardRemaining(a, tierSource) {
  if (tierSource === "fallback" || tierSource === "loading") return null;
  if (a.remaining != null && Number.isFinite(Number(a.remaining))) return Math.max(0, Number(a.remaining));
  return Math.max(0, Number(a.supply) || 0);
}

function cardMaxPerCheckout(a, remaining) {
  if (a.maxPerCheckout != null && Number.isFinite(Number(a.maxPerCheckout))) {
    return Math.max(1, Math.floor(Number(a.maxPerCheckout)));
  }
  return Math.max(1, Math.min(50, remaining || 1));
}

function cardCanCheckout(a, status, remaining) {
  if (a.checkoutEnabled === false) return false;
  if (status !== "Available") return false;
  if (remaining < 1) return false;
  if (a.priceLabel === "TBD") return false;
  if (a.checkoutEnabled === true) return true;
  return a.usdcPrice != null || (Number(a.price) > 0 && a.priceLabel !== "TBD");
}

function tierSlugOf(a) {
  return String(a?.tierSlug || a?.id || "").trim();
}

/**
 * Inventory gates for wallet CTA copy + handler (Comic-style: do not block wallet mint when `tierSource === "fallback"`; prepare + chain are authoritative).
 */
function walletSoftGateKey(a, qty) {
  if (a.status === "Unavailable") return "tier_unavailable";
  if (Number(a._stock) < Number(qty)) return "insufficient_stock";
  if (a.priceLabel === "TBD") return "price_pending";
  return null;
}

function walletSoftGateUserMessage(key) {
  switch (key) {
    case "tier_unavailable":
      return "Wallet mint unavailable for this tier.";
    case "insufficient_stock":
      return "Not enough remaining inventory for this quantity.";
    case "price_pending":
      return "Token price setup pending for this tier.";
    default:
      return "";
  }
}

function cardCheckoutDisabledReason(a, status, stock) {
  if (a.checkoutEnabled === false) {
    return a.disabledReason || "This property tier is unavailable.";
  }
  if (status !== "Available") return `This property tier is unavailable.`;
  if (stock < 1) return "This property tier is unavailable.";
  if (a.priceLabel === "TBD") return "This property tier is unavailable.";
  if (!cardCanCheckout(a, status, stock)) return "This property tier is unavailable.";
  return "";
}

function normalizeWeb3Error(error) {
  const raw = error?.shortMessage || error?.reason || error?.message || "";
  const lowered = String(raw).toLowerCase();
  if (
    lowered.includes("user rejected") ||
    lowered.includes("user denied") ||
    lowered.includes("rejected the request") ||
    error?.code === 4001
  ) {
    return "Wallet request cancelled.";
  }
  if (lowered.includes("cannot estimate gas") || lowered.includes("execution reverted")) {
    return "Transaction reverted. Check token availability, price, and selected network.";
  }
  return raw || "Transaction failed.";
}

/** Safe stage logs for Mark’s real browser (no JWTs / keys / RPC secrets). */
function osharWalletMintLog(stage, payload) {
  if (typeof console === "undefined" || typeof console.info !== "function") return;
  console.info(`[oshar-wallet-mint] ${stage}`, payload || {});
}

async function readInjectedChainId() {
  if (!window?.ethereum?.request) return null;
  try {
    const hex = await window.ethereum.request({ method: "eth_chainId" });
    return parseInt(String(hex), 16);
  } catch {
    return null;
  }
}

function mapWalletFlowErrorMessage(error) {
  if (!error) return "Wallet mint failed.";
  if (error.name === "AbortError" || error.isAbortTimeout) {
    return "Wallet mint prepare timed out before the Treydora API returned a response.";
  }

  const status = error.httpStatus != null ? Number(error.httpStatus) : null;
  if (status === 401) {
    const m = String(error.message || error.responseMessage || "").trim();
    return m || "Please sign in again before wallet minting.";
  }
  if (status === 403) {
    const m = String(error.message || error.responseMessage || "").trim();
    return m || "You don't have permission to complete this wallet mint.";
  }
  if (status === 400 || status === 404) {
    const m = String(error.message || error.responseMessage || "").trim();
    return m || "Invalid wallet mint request.";
  }
  if (status === 409) {
    const m = String(error.message || error.responseMessage || "").trim();
    return m || "No mintable inventory available for this tier.";
  }
  if (status != null && status >= 500) {
    return "Wallet mint prepare failed on the Treydora API.";
  }

  if (error.isNetworkFailure) {
    return "Treydora API is unavailable. Start the backend or check API base URL.";
  }

  const raw = String(error?.message || error || "");
  const low = raw.toLowerCase();

  if (low.includes("please log in to prepare") || low.includes("please log in to mint")) {
    return "Please sign in before wallet minting.";
  }
  if (low.includes("please sign in before wallet minting")) {
    return "Please sign in before wallet minting.";
  }
  if (low.includes("please log in")) {
    return "Please sign in before wallet minting.";
  }
  if (
    low.includes("session expired") ||
    low.includes("invalid token") ||
    low.includes("log out and sign in") ||
    low.includes("session was rejected") ||
    low.includes("valid token to access") ||
    low.includes("provide a valid token")
  ) {
    return "Your session expired. Please log in again.";
  }

  const looksLikeBrowserNetworkFailure =
    status == null &&
    (low.includes("failed to fetch") ||
      low.includes("networkerror when attempting to fetch") ||
      low.includes("load failed") ||
      low.includes("err_connection_refused") ||
      low.includes("err_network_changed") ||
      low.includes("network request failed") ||
      (low.includes("fetch") && low.includes("cors")));

  if (looksLikeBrowserNetworkFailure) {
    return "Treydora API is unavailable. Start the backend or check API base URL.";
  }

  return normalizeWeb3Error(error) || raw;
}

/**
 * @param {"api"|"fallback"|"loading"} tierSource — "api" when GET /api/oshar-real-estate/tiers succeeded; "loading" while fetching; otherwise bundled data (no live inventory).
 */
const PropertyTypeGrid = ({
  assetTypes,
  onAuthRequired,
  scrollTargetId,
  tierSource = "api",
  tiersHttpMeta = null,
}) => {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const debugPurchase = searchParams.get("debugPurchase") === "1";
  const showWalletDiagnostics = hasOsharWalletDebugFlags(searchParams);

  const { address, isConnected } = useAccount();
  const { connectAsync, connectors, isLoading: isConnectingWallet } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork, isLoading: isSwitchingNetwork } = useSwitchNetwork();

  const [chainStatusMap, setChainStatusMap] = useState({});
  const [quantityById, setQuantityById] = useState({});
  const [legalAckById, setLegalAckById] = useState({});
  /** When user clicks purchase without accepting terms, pulse the inline legal block (no modal). */
  const [legalHighlightById, setLegalHighlightById] = useState({});
  const [payStatusById, setPayStatusById] = useState({});
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null);
  const [walletLoadingId, setWalletLoadingId] = useState(null);
  const [walletMsgById, setWalletMsgById] = useState({});
  const [walletPhaseById, setWalletPhaseById] = useState({});
  const [walletEthTotalById, setWalletEthTotalById] = useState({});
  /** `?debugPurchase=1` only — wallet CTA diagnostics (no secrets). */
  const [walletUiDebugById, setWalletUiDebugById] = useState({});
  /** Visible wallet-mint diagnostic rows (safe fields only). */
  const [walletMintDiagById, setWalletMintDiagById] = useState({});
  /** Show diagnostic box when ?debugPurchase=1 or after a wallet-mint error/stop. */
  const [walletMintDiagVisibleById, setWalletMintDiagVisibleById] = useState({});
  /** After on-chain mint OK but dashboard sync failed — show retry (key = tier card id). */
  const [walletDashboardSyncPendingById, setWalletDashboardSyncPendingById] = useState({});
  const [debugSnapById, setDebugSnapById] = useState({});
  const { isAuthenticated, walletLogin, linkWallet } = useAuth();
  const { signMessageAsync } = useSignMessage();
  const tokenPresent = Boolean(getAuthToken());

  /** Prevents overlapping prepare/mint runs for the same card (state updates are async). */
  const walletHardOpRef = useRef(null);

  const hasInjectedWallet = typeof window !== "undefined" && Boolean(window.ethereum);
  const wrongNetwork = isWrongNetwork(chain?.id);

  const walletMintPrepareUrl = useMemo(() => {
    const b = String(getOsharApiBaseUrl() || "").trim().replace(/\/$/, "");
    return b ? `${b}/api/oshar-real-estate/wallet-mint/prepare` : "";
  }, []);

  const walletMintSyncUrl = useMemo(() => {
    const b = String(getOsharApiBaseUrl() || "").trim().replace(/\/$/, "");
    return b ? `${b}/api/oshar-real-estate/sync-wallet-mint` : "";
  }, []);

  const registryOfflineHint = useMemo(() => {
    if (tierSource === "api") return "GET /tiers succeeded (backend tier cards)";
    if (tierSource === "loading") return "GET /tiers in progress";
    if (!tiersHttpMeta) return "GET /tiers meta not loaded yet";
    if (tiersHttpMeta.ok) return "GET /tiers ok but catalog empty or unmapped (showing fallback cards)";
    if (tiersHttpMeta.errorKey === "network") {
      return "GET /tiers network/CORS failure — wallet mint still tries prepare against API base";
    }
    return `GET /tiers not ok: ${tiersHttpMeta.errorKey || "?"} (http ${tiersHttpMeta.httpStatus ?? "—"})`;
  }, [tierSource, tiersHttpMeta]);

  useEffect(() => {
    const next = {};
    assetTypes.forEach((a) => {
      next[a.id] = 1;
    });
    setQuantityById(next);
  }, [assetTypes]);

  useEffect(() => {
    const rec = readPendingOsharWalletMintRecord();
    const gid = rec && rec.gridTierId != null ? String(rec.gridTierId).trim() : "";
    if (!gid) return;
    setWalletDashboardSyncPendingById((prev) => ({ ...prev, [gid]: { fromStorage: true } }));
    setWalletMsgById((prev) => ({
      ...prev,
      [gid]: "NFT minted successfully. Dashboard sync is pending.",
    }));
  }, [assetTypes]);

  useEffect(() => {
    let mounted = true;
    const neutral = () => {
      const next = {};
      assetTypes.forEach((a) => {
        next[a.id] = { checking: false, minted: false, owner: "" };
      });
      return next;
    };

    const checkChainStatus = async () => {
      const withToken = assetTypes.filter(
        (a) => Number.isInteger(Number(a.tokenId)) && Number(a.supply) === 1
      );
      if (!withToken.length) {
        if (mounted) setChainStatusMap(neutral());
        return;
      }
      const nextMap = neutral();
      withToken.forEach((a) => {
        nextMap[a.id] = { checking: true, minted: false, owner: "" };
      });
      if (mounted) setChainStatusMap(nextMap);

      await Promise.all(
        withToken.map(async (asset) => {
          const tokenId = Number(asset.tokenId);
          try {
            const owner = await readOwnerOf(tokenId);
            nextMap[asset.id] = { checking: false, minted: true, owner };
          } catch {
            nextMap[asset.id] = { checking: false, minted: false, owner: "" };
          }
        })
      );
      if (mounted) setChainStatusMap({ ...nextMap });
    };
    checkChainStatus();
    return () => {
      mounted = false;
    };
  }, [assetTypes]);

  const bumpQty = useCallback((id, delta, maxSel) => {
    setQuantityById((prev) => {
      const cur = Number(prev[id]) || 1;
      const next = Math.min(maxSel, Math.max(1, cur + delta));
      return { ...prev, [id]: next };
    });
  }, []);

  const setQtyRaw = useCallback((id, raw, maxSel) => {
    const n = parseInt(String(raw).replace(/\D/g, ""), 10);
    const v = Number.isFinite(n) ? Math.min(maxSel, Math.max(1, n)) : 1;
    setQuantityById((prev) => ({ ...prev, [id]: v }));
  }, []);

  const cards = useMemo(
    () =>
      assetTypes.map((asset) => {
        const chain = chainStatusMap[asset.id] || { checking: false, minted: false, owner: "" };
        const cardState = resolveOsharRealEstateCardState({
          asset,
          tierSource,
          chainStatus: chain,
          stripeFulfillmentReady: asset.stripeFulfillmentReady === true,
          tokenPresent,
        });
        return {
          ...asset,
          chain,
          status: cardState.badgeLabel,
          cardState,
          _remaining: cardState._remaining,
          _stock: cardState._stock,
          _maxSel: cardState._maxSel,
          _canCheckout: cardState.canUseCard,
          _checkoutDisabledReason: cardState.disabledReason,
        };
      }),
    [assetTypes, chainStatusMap, tierSource, tokenPresent]
  );

  const setLegalField = useCallback((id, field, value) => {
    setLegalAckById((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
    setLegalHighlightById((prev) => ({ ...prev, [id]: false }));
  }, []);

  const handlePayWithCard = useCallback(
    async (a, qty) => {
      const id = a.id;
      const slug = tierSlugOf(a);
      const legal = legalAckById[id] || { termsOk: false, vrOk: false };
      const termsOk = Boolean(legal.termsOk);
      const vrOk = Boolean(legal.vrOk);

      setPayStatusById((prev) => ({ ...prev, [id]: "" }));
      setDebugSnapById((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          lastAction: "Pay with Card",
          requestFired: false,
        },
      }));

      if (!getAuthToken()) {
        onAuthRequired?.(a);
        setPayStatusById((prev) => ({
          ...prev,
          [id]: "Please log in again to purchase.",
        }));
        return;
      }

      if (!termsOk || !vrOk) {
        setLegalHighlightById((prev) => ({ ...prev, [id]: true }));
        setPayStatusById((prev) => ({
          ...prev,
          [id]: "Please accept both purchase terms.",
        }));
        return;
      }

      if (!slug) {
        setPayStatusById((prev) => ({
          ...prev,
          [id]: "This property tier is unavailable.",
        }));
        return;
      }

      const apiBase = String(getOsharApiBaseUrl() || "").trim();
      if (!apiBase) {
        setPayStatusById((prev) => ({
          ...prev,
          [id]: "Checkout service unavailable.",
        }));
        return;
      }

      if (!a._canCheckout || !a.cardState?.canUseCard) {
        setPayStatusById((prev) => ({
          ...prev,
          [id]: a._checkoutDisabledReason || a.cardState?.primaryMessage || "This property tier is unavailable.",
        }));
        return;
      }

      const maxSel = a._maxSel;
      const q = Math.min(maxSel, Math.max(1, Number(qty) || 1));
      if (!Number.isInteger(q) || q < 1) {
        setPayStatusById((prev) => ({ ...prev, [id]: "Select a valid quantity." }));
        return;
      }
      if (q > maxSel) {
        setPayStatusById((prev) => ({
          ...prev,
          [id]: `Only ${maxSel} remaining.`,
        }));
        return;
      }

      setCheckoutLoadingId(id);
      setPayStatusById((prev) => ({ ...prev, [id]: "Preparing secure checkout..." }));
      trackCheckoutStarted({
        productType: "oshar_real_estate",
        tierSlug: slug,
        quantity: q,
      });
      setDebugSnapById((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          requestFired: true,
          lastResponseStatus: null,
        },
      }));

      try {
        const session = await createOsharCheckoutSession({
          tierSlug: slug,
          quantity: q,
        });
        const url = typeof session?.url === "string" ? session.url.trim() : "";
        if (!url) {
          setPayStatusById((prev) => ({
            ...prev,
            [id]: "Unable to start secure checkout. Missing checkout URL.",
          }));
          setDebugSnapById((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), lastResponseStatus: 200, requestFired: true },
          }));
          return;
        }
        setPayStatusById((prev) => ({ ...prev, [id]: "Redirecting to Stripe..." }));
        window.location.assign(url);
      } catch (error) {
        const msg = error?.message || "Unable to start secure checkout. Please try again.";
        setPayStatusById((prev) => ({ ...prev, [id]: msg }));
        setDebugSnapById((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), lastResponseStatus: "error", requestFired: true },
        }));
      } finally {
        setCheckoutLoadingId((cur) => (cur === id ? null : cur));
      }
    },
    [legalAckById, onAuthRequired]
  );

  const connectWalletOnly = useCallback(
    async (tierCardId) => {
      const id = String(tierCardId || "").trim();
      if (!id) return;
      if (!hasInjectedWallet) {
        setWalletMsgById((prev) => ({
          ...prev,
          [id]: "Install MetaMask or open it in this browser, then press Connect Wallet.",
        }));
        return;
      }
      setWalletMsgById((prev) => ({ ...prev, [id]: "Connecting wallet…" }));
      try {
        if (!window?.ethereum?.request) {
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: "Install MetaMask or open it in this browser, then press Connect Wallet.",
          }));
          return;
        }
        const connector =
          (Array.isArray(connectors) ? connectors : []).find((c) => c?.ready) || connectors?.[0];
        if (!connector) {
          setWalletMsgById((prev) => ({ ...prev, [id]: "No wallet connector available." }));
          return;
        }
        // One provider account request: InjectedConnector.connect uses eth_requestAccounts once.
        // Do not call eth_requestAccounts and connectAsync back-to-back (duplicate MetaMask prompts).
        let walletAddr = "";
        if (!isConnected) {
          const res = await connectAsync({ connector });
          walletAddr = String(res?.account || "").trim();
        } else {
          const accounts = await window.ethereum.request({ method: "eth_accounts" });
          walletAddr = String(accounts?.[0] || address || "").trim();
        }
        setWalletMsgById((prev) => ({
          ...prev,
          [id]: walletAddr
            ? `Connected ${shortenAddress(walletAddr)}. Accept terms to continue, then mint.`
            : "Wallet connected. Accept terms to continue, then mint.",
        }));
      } catch (e) {
        setWalletMsgById((prev) => ({ ...prev, [id]: normalizeWeb3Error(e) }));
      }
    },
    [address, connectAsync, connectors, hasInjectedWallet, isConnected]
  );

  const handleRetryOsharDashboardSync = useCallback(async (tierCardId) => {
    const tid = String(tierCardId || "").trim();
    if (!tid) return;
    setWalletLoadingId(tid);
    setWalletMsgById((prev) => ({ ...prev, [tid]: "Retrying dashboard sync…" }));
    try {
      await retryPendingOsharWalletSync();
      setWalletDashboardSyncPendingById((prev) => {
        const next = { ...prev };
        delete next[tid];
        return next;
      });
      setWalletPhaseById((prev) => ({
        ...prev,
        [tid]: { stage: "done", total: Number(prev[tid]?.total) > 0 ? Number(prev[tid].total) : 1 },
      }));
      setWalletMsgById((prev) => ({
        ...prev,
        [tid]: "Dashboard sync complete. Open the dashboard to view My Oshar Properties.",
      }));
    } catch (e) {
      const m = String(e?.message || e || "Retry failed.");
      setWalletMsgById((prev) => ({
        ...prev,
        [tid]: `NFT minted successfully. Dashboard sync is still pending.\n${m}`,
      }));
    } finally {
      setWalletLoadingId((cur) => (cur === tid ? null : cur));
    }
  }, []);

  const executeWalletMint = useCallback(
    async (a, qty) => {
      const id = a.id;
      const slug = tierSlugOf(a);
      const apiBaseRaw = String(getOsharApiBaseUrl() || "").trim();
      const prepUrl = apiBaseRaw
        ? `${apiBaseRaw.replace(/\/$/, "")}/api/oshar-real-estate/wallet-mint/prepare`
        : "(no base URL)";
      const q = Math.min(a._maxSel, Math.max(1, Number(qty) || 1));
      const tokenHere = Boolean(getAuthToken());
      const mmDet = hasInjectedWallet;
      const chainIdStr = chain?.id != null ? String(chain.id) : "";

      const patchDebug = (partial) =>
        setWalletUiDebugById((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), ...partial },
        }));

      const diagBase = {
        clickFired: "yes",
        signedInTokenPresent: tokenHere ? "yes" : "no",
        apiBaseUrl: apiBaseRaw || "(empty)",
        selectedTierId: String(id),
        selectedTierSlug: slug || "(none)",
        quantity: String(q),
        prepareAttempted: "no",
        prepareUrl: prepUrl,
        prepareStatus: "—",
        prepareErrorName: "—",
        prepareErrorMessage: "—",
        prepareResponseMessage: "—",
        metamaskDetected: mmDet ? "yes" : "no",
        metamaskOpenAttempted: "no",
        chainId: chainIdStr,
        contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
        syncAttempted: "no",
        syncStatus: "—",
      };

      flushSync(() => {
        if (showWalletDiagnostics) {
          setWalletMintDiagById((prev) => ({ ...prev, [id]: { ...diagBase } }));
          setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
        }
        setWalletMsgById((prev) => ({
          ...prev,
          [id]: "Preparing wallet mint…",
        }));
        setWalletPhaseById((prev) => ({ ...prev, [id]: null }));
      });

      osharWalletMintLog("click", {
        selectedTierId: id,
        selectedTierSlug: slug || null,
        quantity: q,
        apiBaseUrl: apiBaseRaw || "(empty)",
        attemptedUrl: prepUrl,
        prepareUrl: prepUrl,
        signedInTokenPresent: tokenHere ? "yes" : "no",
        metamaskDetected: mmDet ? "yes" : "no",
        chainId: chainIdStr,
        contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
      });

      const legal = legalAckById[id] || { termsOk: false, vrOk: false };
      const termsOk = Boolean(legal.termsOk);
      const vrOk = Boolean(legal.vrOk);

      if (walletDashboardSyncPendingById[id]) {
        flushSync(() => {
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: "Dashboard sync is still pending for your last mint. Use “Retry Dashboard Sync” below, then try again.",
          }));
        });
        return;
      }

      patchDebug({
        lastWalletClickReached: true,
        walletStatus: "Wallet handler reached",
        lastWalletAction: "mint_cta_click",
        prepareRequestFired: false,
        prepareOk: false,
        prepareBlockers: "",
        prepareTokenIds: "",
        prepareError: "",
        prepareEthSample: "",
      });

      if (!termsOk || !vrOk) {
        flushSync(() => {
          if (showWalletDiagnostics) {
            setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
            setWalletMintDiagById((prev) => ({
              ...prev,
              [id]: {
                ...(prev[id] || diagBase),
                prepareAttempted: "no",
                prepareErrorName: "Validation",
                prepareErrorMessage: "Purchase terms not accepted.",
              },
            }));
          }
          setLegalHighlightById((prev) => ({ ...prev, [id]: true }));
          setWalletMsgById((prev) => ({ ...prev, [id]: "Please accept both purchase terms." }));
        });
        patchDebug({ lastWalletError: "legal_unchecked" });
        return;
      }

      if (!a.cardState?.canUseWallet) {
        const msg = a.cardState?.primaryMessage || "Wallet mint unavailable for this tier.";
        flushSync(() => {
          setWalletMsgById((prev) => ({ ...prev, [id]: msg }));
        });
        patchDebug({ lastWalletError: "tier_not_wallet_ready" });
        return;
      }

      if (!slug) {
        flushSync(() => {
          if (showWalletDiagnostics) {
            setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
            setWalletMintDiagById((prev) => ({
              ...prev,
              [id]: {
                ...(prev[id] || diagBase),
                prepareAttempted: "no",
                prepareErrorName: "Validation",
                prepareErrorMessage: "Missing tier slug.",
              },
            }));
          }
          setWalletMsgById((prev) => ({ ...prev, [id]: "This property tier is unavailable." }));
        });
        patchDebug({ lastWalletError: "missing_tier_slug" });
        return;
      }

      if (!hasInjectedWallet) {
        flushSync(() => {
          setWalletMsgById((prev) => ({ ...prev, [id]: "Install MetaMask or open it in this browser." }));
        });
        patchDebug({ lastWalletError: "no_metamask" });
        return;
      }

      const apiBase = apiBaseRaw;
      if (!apiBase) {
        flushSync(() => {
          if (showWalletDiagnostics) {
            setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
            setWalletMintDiagById((prev) => ({
              ...prev,
              [id]: {
                ...(prev[id] || diagBase),
                prepareAttempted: "no",
                prepareUrl: "(no base URL)",
                prepareErrorName: "Config",
                prepareErrorMessage: "REACT_APP_API_BASE_URL is empty.",
              },
            }));
          }
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: "Treydora API base URL is not configured; cannot prepare wallet mint.",
          }));
        });
        patchDebug({ lastWalletError: "missing_api_base" });
        return;
      }

      let walletAddr = String(address || "").trim();
      if (!isConnected || !walletAddr) {
        setWalletMsgById((prev) => ({ ...prev, [id]: "Connect Wallet first, then mint." }));
        patchDebug({ lastWalletError: "wallet_not_connected" });
        return;
      }

      try {
        const needsLogin = !getAuthToken();
        flushSync(() => {
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: needsLogin ? WALLET_SIGN_IN_HINT : WALLET_LINK_HINT,
          }));
        });
        osharWalletMintLog("wallet_auth_begin", {
          selectedTierId: id,
          selectedTierSlug: slug || null,
          hadToken: needsLogin ? "no" : "yes",
        });
        const authMode = await ensureTreydoraWalletSession({
          walletAddress: walletAddr,
          signMessageAsync,
          walletLogin,
          linkWallet,
        });
        osharWalletMintLog("wallet_auth_ok", {
          selectedTierId: id,
          mode: authMode,
        });
        flushSync(() => {
          setWalletMintDiagById((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] || diagBase),
              signedInTokenPresent: "yes",
            },
          }));
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: `${WALLET_SIGN_IN_SUBHINT} Preparing mint…`,
          }));
        });
      } catch (authErr) {
        const msg = formatWalletSessionError(authErr);
        flushSync(() => {
          if (showWalletDiagnostics) {
            setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
            setWalletMintDiagById((prev) => ({
              ...prev,
              [id]: {
                ...(prev[id] || diagBase),
                signedInTokenPresent: getAuthToken() ? "yes" : "no",
                prepareAttempted: "no",
                prepareErrorName: String(authErr?.name || "WalletAuthError"),
                prepareErrorMessage: String(msg).slice(0, 400),
              },
            }));
          }
          setWalletMsgById((prev) => ({ ...prev, [id]: msg }));
        });
        patchDebug({ lastWalletError: "wallet_auth_failed", prepareError: msg });
        osharWalletMintLog("wallet_auth_failed", {
          selectedTierId: id,
          errorMessage: String(msg).slice(0, 200),
        });
        return;
      }

      let chainIdLive = await readInjectedChainId();
      if (chainIdLive == null && chain?.id != null) {
        chainIdLive = Number(chain.id);
      }
      if (Number(chainIdLive) !== OSHAR_REAL_ESTATE_CHAIN_ID) {
        if (switchNetwork) {
          try {
            switchNetwork(OSHAR_REAL_ESTATE_CHAIN_ID);
          } catch {
            /* same as LibraryNftV2DirectMintControls: non-fatal; user may reject or chain unsupported */
          }
        }
        flushSync(() => {
          setWalletMintDiagById((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] || diagBase),
              chainId: String(chainIdLive ?? ""),
              prepareAttempted: "no",
            },
          }));
        });
        setWalletMsgById((prev) => ({
          ...prev,
          [id]: `${getSwitchToNetworkLabel(OSHAR_REAL_ESTATE_CHAIN_ID)} in your wallet, then press Mint with Wallet again.`,
        }));
        patchDebug({ lastWalletError: "wrong_chain_prompt" });
        osharWalletMintLog("chain_switch_prompt", {
          selectedTierId: id,
          chainId: chainIdLive,
          targetChainId: OSHAR_REAL_ESTATE_CHAIN_ID,
        });
        return;
      }

      try {
        walletHardOpRef.current = id;
        setWalletLoadingId(id);
        setWalletPhaseById((prev) => ({ ...prev, [id]: { stage: "preparing" } }));
        setWalletMsgById((prev) => ({
          ...prev,
          [id]: "Preparing Mint…",
        }));
        patchDebug({ prepareRequestFired: true, prepareRequestUrl: walletMintPrepareUrl || "(empty base)" });

        flushSync(() => {
          setWalletMintDiagById((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] || diagBase),
              prepareAttempted: "yes",
              prepareUrl: walletMintPrepareUrl || prepUrl,
              prepareStatus: "pending",
              chainId: String(OSHAR_REAL_ESTATE_CHAIN_ID),
              metamaskOpenAttempted: "no",
            },
          }));
        });

        osharWalletMintLog("prepare_begin", {
          selectedTierId: id,
          selectedTierSlug: slug,
          quantity: q,
          apiBaseUrl: apiBase,
          attemptedUrl: walletMintPrepareUrl || prepUrl,
          chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
          contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
        });

        /** Backend prepare may scan chain RPC for many slots — keep below RPC-heavy worst case. */
        const timeoutMs = 120000;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        let prep;
        try {
          prep = await prepareOsharWalletMint({
            tierSlug: slug,
            quantity: q,
            walletAddress: walletAddr,
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeoutId);
        }

        const blockersStr = Array.isArray(prep?.blockers) ? prep.blockers.join("; ") : "";
        patchDebug({
          prepareOk: Boolean(prep),
          prepareBlockers: blockersStr,
          prepareTokenIds: Array.isArray(prep?.tokenIds) ? prep.tokenIds.map(String).join(",") : "",
          prepareEthSample: String(prep?.priceWei || prep?.pricesWei?.[0] || ""),
        });

        flushSync(() => {
          setWalletMintDiagById((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] || diagBase),
              prepareAttempted: "yes",
              prepareUrl: walletMintPrepareUrl || prepUrl,
              prepareStatus: "200",
              prepareResponseMessage: blockersStr || String(prep?.message || "").slice(0, 400) || "(ok)",
              prepareErrorName: "—",
              prepareErrorMessage: "—",
              chainId: String(OSHAR_REAL_ESTATE_CHAIN_ID),
              metamaskOpenAttempted: "no",
            },
          }));
        });

        osharWalletMintLog("prepare_success", {
          selectedTierId: id,
          selectedTierSlug: slug,
          quantity: q,
          attemptedUrl: walletMintPrepareUrl || prepUrl,
          httpStatus: 200,
          responseMessage: blockersStr ? String(blockersStr).slice(0, 200) : "ok",
          responseBodyKeys: prep && typeof prep === "object" ? Object.keys(prep).slice(0, 20) : [],
          chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
          contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
        });

        const tokenIds = Array.isArray(prep.tokenIds) ? prep.tokenIds.map((t) => Math.floor(Number(t))) : [];
        const pricesWei = Array.isArray(prep.pricesWei)
          ? prep.pricesWei.map((p) => String(p))
          : tokenIds.map(() => String(prep.priceWei || "0"));

        if (!tokenIds.length) {
          throw new Error("Wallet mint unavailable.");
        }

        for (let i = 0; i < tokenIds.length; i++) {
          const ws = pricesWei[i] || prep.priceWei;
          let priceWei;
          try {
            priceWei = ethers.BigNumber.from(ws);
          } catch {
            throw new Error("Token price setup pending.");
          }
          if (priceWei.isZero()) {
            throw new Error("Token price setup pending.");
          }
        }

        if (prep.totalPriceWei) {
          try {
            setWalletEthTotalById((prev) => ({
              ...prev,
              [id]: `${ethers.utils.formatEther(prep.totalPriceWei)} ETH (wallet total)`,
            }));
          } catch {
            /* ignore */
          }
        }

        if (tokenIds.length > 1) {
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: `Sequential wallet mint: MetaMask will open once per token (${tokenIds.length} total).`,
          }));
        }

        let completed = 0;
        let abortedForDashboardSync = false;
        try {
          for (let i = 0; i < tokenIds.length; i++) {
            const tid = tokenIds[i];
            const weiStr = pricesWei[i] || prep.priceWei;
            const priceWei = ethers.BigNumber.from(weiStr);
            const n = i + 1;
            const total = tokenIds.length;
            setWalletPhaseById((prev) => ({ ...prev, [id]: { stage: "minting", current: n, total } }));
            setWalletMsgById((prev) => ({
              ...prev,
              [id]: total > 1 ? `Confirm in MetaMask (${n} of ${total})` : "Confirm in MetaMask",
            }));
            flushSync(() => {
              setWalletMintDiagById((prev) => ({
                ...prev,
                [id]: {
                  ...(prev[id] || diagBase),
                  metamaskOpenAttempted: "yes",
                },
              }));
            });
            osharWalletMintLog("metamask_begin", {
              stage: "mint",
              selectedTierId: id,
              selectedTierSlug: slug,
              tokenId: tid,
              step: `${n}/${total}`,
              chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
              contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
            });
            let txHash;
            try {
              ({ txHash } = await mintWithETH(tid, priceWei, {
                afterSubmit: (tx) => {
                  try {
                    writePendingOsharWalletMintRecord({
                      txHash: tx.hash,
                      chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
                      contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
                      tierSlug: slug,
                      tokenIds: [tid],
                      quantity: q,
                      walletAddress: walletAddr,
                      timestamp: Date.now(),
                      gridTierId: id,
                    });
                  } catch {
                    /* ignore */
                  }
                  setWalletMsgById((prev) => ({
                    ...prev,
                    [id]:
                      total > 1
                        ? `Confirming Purchase… (${n} of ${total})`
                        : "Confirming Purchase…",
                  }));
                },
              }));
            } catch (mmErr) {
              clearPendingOsharWalletMintRecord();
              flushSync(() => {
                if (showWalletDiagnostics) {
                  setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
                  setWalletMintDiagById((prev) => ({
                    ...prev,
                    [id]: {
                      ...(prev[id] || diagBase),
                      metamaskOpenAttempted: "yes",
                      prepareErrorName: String(mmErr?.name || "MetaMaskError"),
                      prepareErrorMessage: String(normalizeWeb3Error(mmErr)).slice(0, 400),
                    },
                  }));
                }
              });
              osharWalletMintLog("metamask_failed", {
                stage: "mint",
                tokenId: tid,
                errorName: mmErr?.name,
                errorMessage: String(mmErr?.message || "").slice(0, 300),
                chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
                contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
              });
              throw mmErr;
            }
            setWalletPhaseById((prev) => ({ ...prev, [id]: { stage: "syncing", current: n, total } }));
            setWalletMsgById((prev) => ({
              ...prev,
              [id]:
                total > 1
                  ? `Syncing to Dashboard… (${n} of ${total})`
                  : "Syncing to Dashboard…",
            }));
            flushSync(() => {
              setWalletMintDiagById((prev) => ({
                ...prev,
                [id]: {
                  ...(prev[id] || diagBase),
                  syncAttempted: "yes",
                  syncStatus: "pending",
                },
              }));
            });
            const txShort =
              txHash && typeof txHash === "string" && txHash.length > 16
                ? `${txHash.slice(0, 10)}…${txHash.slice(-6)}`
                : txHash;
            osharWalletMintLog("sync_begin", {
              attemptedUrl: walletMintSyncUrl,
              txHash: txShort,
              tokenId: tid,
              chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
            });
            try {
              await syncOsharWalletMint({
                txHash,
                chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
                tokenId: tid,
                tierSlug: slug,
                walletAddress: walletAddr,
              });
              clearPendingOsharWalletMintRecord();
            } catch (syncErr) {
              flushSync(() => {
                if (showWalletDiagnostics) {
                  setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
                  setWalletMintDiagById((prev) => ({
                    ...prev,
                    [id]: {
                      ...(prev[id] || diagBase),
                      syncAttempted: "yes",
                    syncStatus:
                      syncErr?.httpStatus != null
                        ? String(syncErr.httpStatus)
                        : syncErr?.isNetworkFailure
                          ? "network"
                          : "error",
                    prepareErrorName: String(syncErr?.errorName || syncErr?.name || "SyncError"),
                    prepareErrorMessage: String(syncErr?.errorMessage || syncErr?.message || "").slice(0, 400),
                    prepareResponseMessage: String(syncErr?.responseMessage || "").slice(0, 400),
                  },
                }));
                }
              });
              osharWalletMintLog("sync_failed", {
                attemptedUrl: syncErr?.attemptedUrl || walletMintSyncUrl,
                httpStatus: syncErr?.httpStatus ?? null,
                responseMessage: String(syncErr?.responseMessage || syncErr?.message || "").slice(0, 300),
                errorName: syncErr?.errorName || syncErr?.name,
                isNetworkFailure: Boolean(syncErr?.isNetworkFailure),
              });
              const detail = String(syncErr?.responseMessage || syncErr?.message || "").trim();
              setWalletDashboardSyncPendingById((prev) => ({ ...prev, [id]: { backendDetail: detail } }));
              setWalletMsgById((prev) => ({
                ...prev,
                [id]: detail
                  ? `NFT minted successfully. Dashboard sync is pending.\n${detail}`
                  : "NFT minted successfully. Dashboard sync is pending.",
              }));
              abortedForDashboardSync = true;
              break;
            }
            flushSync(() => {
              setWalletMintDiagById((prev) => ({
                ...prev,
                [id]: {
                  ...(prev[id] || {}),
                  syncStatus: "200",
                },
              }));
            });
            osharWalletMintLog("sync_success", {
              attemptedUrl: walletMintSyncUrl,
              httpStatus: 200,
              tokenId: tid,
            });
            setWalletMsgById((prev) => ({ ...prev, [id]: `Minted ${n} of ${total}` }));
            completed = n;
          }
          if (abortedForDashboardSync) {
            setWalletPhaseById((prev) => ({
              ...prev,
              [id]: { stage: "sync_pending", completed, total: tokenIds.length },
            }));
            patchDebug({ lastWalletError: "dashboard_sync_pending", walletStatus: "mint_ok_sync_pending" });
            return;
          }
          setWalletPhaseById((prev) => ({ ...prev, [id]: { stage: "done", total: tokenIds.length } }));
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: "Mint complete. Open the dashboard to view My Oshar Properties.",
          }));
          patchDebug({ lastWalletError: "", walletStatus: "success" });
        } catch (inner) {
          if (completed > 0 && completed < tokenIds.length) {
            const detail = normalizeWeb3Error(inner);
            setWalletPhaseById((prev) => ({
              ...prev,
              [id]: { stage: "partial", completed, total: tokenIds.length },
            }));
            setWalletMsgById((prev) => ({
              ...prev,
              [id]: `Minted ${completed} of ${tokenIds.length} — ${detail} Earlier mint(s) remain on-chain; open the dashboard to confirm sync.`,
            }));
            patchDebug({ lastWalletError: "partial_mint_stop", prepareError: detail });
            if (showWalletDiagnostics) {
              flushSync(() => {
                setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
              });
            }
          } else {
            throw inner;
          }
        }
      } catch (e) {
        const msg = mapWalletFlowErrorMessage(e);
        const syncUrlHit = String(e?.attemptedUrl || "").includes("sync-wallet-mint");
        flushSync(() => {
          if (showWalletDiagnostics) {
            setWalletMintDiagVisibleById((p) => ({ ...p, [id]: true }));
          }
          setWalletMintDiagById((prev) => {
            if (!showWalletDiagnostics) return prev;
            const cur = prev[id] || diagBase;
            const next = { ...cur };
            if (e?.name === "AbortError" || e?.isAbortTimeout) {
              next.prepareAttempted = "yes";
              next.prepareUrl = e.attemptedUrl || cur.prepareUrl;
              next.prepareStatus = "timeout";
              next.prepareErrorName = "AbortError";
              next.prepareErrorMessage = String(e?.errorMessage || e?.message || "").slice(0, 500);
              next.prepareResponseMessage = msg;
              next.metamaskOpenAttempted = "no";
            } else if (syncUrlHit) {
              next.syncAttempted = "yes";
              next.syncStatus =
                e?.httpStatus != null ? String(e.httpStatus) : e?.isNetworkFailure ? "network" : "error";
              next.prepareErrorName = String(e?.errorName || e?.name || "Error");
              next.prepareErrorMessage = String(e?.errorMessage || e?.message || msg).slice(0, 500);
              next.prepareResponseMessage = String(e?.responseMessage || "").slice(0, 500);
            } else if (e?.httpStatus != null || e?.isNetworkFailure || e?.attemptedUrl) {
              next.prepareAttempted = "yes";
              next.prepareUrl = e.attemptedUrl || cur.prepareUrl;
              next.prepareStatus =
                e?.httpStatus != null ? String(e.httpStatus) : e?.isNetworkFailure ? "network" : "error";
              next.prepareErrorName = String(e?.errorName || e?.name || "Error");
              next.prepareErrorMessage = String(e?.errorMessage || e?.message || msg).slice(0, 500);
              next.prepareResponseMessage = String(e?.responseMessage || "").slice(0, 500);
              if (e?.isNetworkFailure) {
                next.metamaskOpenAttempted = "no";
              }
            } else {
              next.prepareErrorName = String(e?.name || "Error");
              next.prepareErrorMessage = String(e?.message || msg).slice(0, 500);
            }
            return { ...prev, [id]: next };
          });
          setWalletMsgById((prev) => ({ ...prev, [id]: msg }));
          setWalletPhaseById((prev) => ({ ...prev, [id]: { stage: "error" } }));
        });
        const lowm = String(e?.message || "").toLowerCase();
        const logLabel = syncUrlHit
          ? "sync_failed"
          : e?.name === "AbortError" || e?.isAbortTimeout
            ? "prepare_failed"
            : e?.httpStatus != null ||
                e?.isNetworkFailure ||
                String(e?.attemptedUrl || "").includes("wallet-mint/prepare") ||
                lowm.includes("wallet mint unavailable") ||
                lowm.includes("token price setup pending")
              ? "prepare_failed"
              : "metamask_failed";
        osharWalletMintLog(logLabel, {
          selectedTierId: id,
          selectedTierSlug: slug,
          quantity: q,
          apiBaseUrl: apiBaseRaw,
          attemptedUrl: e?.attemptedUrl,
          httpStatus: e?.httpStatus ?? null,
          responseMessage: String(e?.responseMessage || e?.message || "").slice(0, 300),
          errorName: e?.errorName || e?.name,
          isNetworkFailure: Boolean(e?.isNetworkFailure),
          responseBodyKeys: Array.isArray(e?.responseBodyKeys) ? e.responseBodyKeys : [],
          chainId: OSHAR_REAL_ESTATE_CHAIN_ID,
          contractAddress: OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
        });
        patchDebug({ lastWalletError: "flow_error", prepareError: msg, prepareOk: false });
      } finally {
        if (walletHardOpRef.current === id) walletHardOpRef.current = null;
        setWalletLoadingId((cur) => (cur === id ? null : cur));
      }
    },
    [
      address,
      chain?.id,
      connectAsync,
      connectors,
      hasInjectedWallet,
      isConnected,
      legalAckById,
      linkWallet,
      signMessageAsync,
      switchNetwork,
      tierSource,
      walletDashboardSyncPendingById,
      walletLogin,
      walletMintPrepareUrl,
      walletMintSyncUrl,
      debugPurchase,
      showWalletDiagnostics,
    ]
  );

  const handleWalletButtonClick = useCallback(
    async (event, a, qty, walletStep) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      const id = a.id;
      const switchLabel = getSwitchToNetworkLabel(OSHAR_REAL_ESTATE_CHAIN_ID);

      switch (walletStep) {
        case "connect":
          await connectWalletOnly(id);
          return;
        case "install":
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: "Install MetaMask or open it in this browser, then press Connect Wallet.",
          }));
          return;
        case "accept_terms":
          setLegalHighlightById((prev) => ({ ...prev, [id]: true }));
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: "Please accept both purchase terms before minting.",
          }));
          return;
        case "switch_network":
          switchNetwork?.(OSHAR_REAL_ESTATE_CHAIN_ID);
          setWalletMsgById((prev) => ({
            ...prev,
            [id]: `${switchLabel} in MetaMask, then press Mint with Wallet.`,
          }));
          return;
        case "retry_sync":
          await handleRetryOsharDashboardSync(id);
          return;
        case "view_dashboard":
          return;
        case "mint":
          await executeWalletMint(a, qty);
          return;
        case "mint_busy":
          return;
        case "blocked":
        default:
          if (a.cardState?.primaryMessage) {
            setWalletMsgById((prev) => ({ ...prev, [id]: a.cardState.primaryMessage }));
          }
          return;
      }
    },
    [connectWalletOnly, executeWalletMint, handleRetryOsharDashboardSync, switchNetwork]
  );

  return (
    <section
      className="oshar-re-grid"
      id={scrollTargetId || "oshar-re-property-types"}
      aria-labelledby="oshar-re-grid-title"
    >
      <div className="oshar-inner">
        <h2 id="oshar-re-grid-title" className="oshar-re-section-title">
          Property Types
        </h2>
        <p className="oshar-re-section-lead">
          Genesis-grade assets across commerce, residence, production, and spectacle — each tier is
          tuned to deepen Oshar without compromising Treydora balance.
        </p>
        {tierSource === "fallback" && (
          <p
            className="oshar-re-card__hint"
            style={{ marginBottom: 16, maxWidth: 720 }}
            role="status"
          >
            {tiersHttpMeta?.ok
              ? "Live tier catalog returned no mappable rows. Showing bundled catalog values; remaining supply may not reflect paid checkouts."
              : tiersHttpMeta?.errorKey === "timeout"
                ? "Live inventory is unavailable (GET /api/oshar-real-estate/tiers timed out). Showing bundled catalog values; card checkout is disabled until the API responds."
              : tiersHttpMeta?.errorKey === "network"
                ? "Live inventory is unavailable (could not reach the Treydora API from this browser). Showing bundled catalog values; card checkout is disabled until GET /api/oshar-real-estate/tiers succeeds."
                : tiersHttpMeta?.httpStatus
                  ? `Live inventory is unavailable (GET /api/oshar-real-estate/tiers returned HTTP ${tiersHttpMeta.httpStatus}). Showing bundled catalog values; card checkout is disabled until the API responds.`
                  : "Live inventory is unavailable. Showing bundled catalog values; card checkout is disabled until GET /api/oshar-real-estate/tiers succeeds."}
          </p>
        )}
        <div className="oshar-re-grid__cards">
          {cards.map((a) => {
            const qty = Math.min(a._maxSel, Math.max(1, Number(quantityById[a.id]) || 1));
            const unit = Number(a.price) || 0;
            const cs = a.cardState;
            const totalLine =
              cs?.canShowQuantity && a.priceLabel !== "TBD" && unit > 0
                ? `Card total: ${a.priceLabel} × ${qty} = $${(unit * qty).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                : null;
            const reserveDisabled = !cs?.canUseCard || checkoutLoadingId === a.id;
            const legal = legalAckById[a.id] || { termsOk: false, vrOk: false };
            const legalComplete = Boolean(legal.termsOk && legal.vrOk);
            const payMsg = payStatusById[a.id] || "";
            const slug = tierSlugOf(a);
            const walletHardBusy = walletLoadingId === a.id;
            const walletMsg = walletMsgById[a.id] || "";
            const waitingConnect = isConnectingWallet && walletMsg.includes("Connecting wallet");
            const waitingSwitch = isSwitchingNetwork && wrongNetwork;
            const walletEthLine = walletEthTotalById[a.id] || "";
            const walletStepParams = {
              cardState: cs,
              hasInjectedWallet,
              isConnected,
              wrongNetwork,
              legalComplete,
              walletPhase: walletPhaseById[a.id],
              walletLoadingId,
              tierCardId: a.id,
              walletDashboardSyncPending: Boolean(walletDashboardSyncPendingById[a.id]),
              isConnectingWallet: waitingConnect,
              isSwitchingNetwork: waitingSwitch,
            };
            const walletStep = resolveOsharWalletStep(walletStepParams);
            const walletLabel = resolveOsharWalletButtonLabel({
              ...walletStepParams,
              switchNetworkLabel: getSwitchToNetworkLabel(OSHAR_REAL_ESTATE_CHAIN_ID),
            });
            const walletPrimaryVisual =
              cs?.canUseWallet &&
              legalComplete &&
              hasInjectedWallet &&
              isConnected &&
              !wrongNetwork &&
              !walletHardBusy;
            const isWalletActionInFlight = walletHardBusy || waitingConnect || waitingSwitch;
            const wDbg = walletUiDebugById[a.id] || {};
            const cardButtonLabel =
              checkoutLoadingId === a.id ? "Preparing secure checkout..." : cs?.cardButtonLabel || "Pay with Card";
            return (
              <article key={a.id} className="oshar-re-card">
                <div className="oshar-re-card__media">
                  <img src={VR_RE_IMAGES[a.imageKey]} alt="" loading="lazy" decoding="async" />
                  <div className="oshar-re-card__shine" aria-hidden />
                </div>
                <div className="oshar-re-card__body">
                  <h3 className="oshar-re-card__title">{a.name}</h3>
                  <span
                    className={`oshar-re-status oshar-re-card__status ${statusClass(a.status, cs?.status)}`}
                  >
                    {a.status}
                  </span>
                  {cs?.primaryMessage ? (
                    <p className="oshar-re-card__hint" role="status">
                      {cs.primaryMessage}
                    </p>
                  ) : null}
                  {isConnected && address ? (
                    <p className="oshar-re-card__hint">
                      Wallet: {shortenAddress(address)}
                    </p>
                  ) : null}
                  {a.chain.checking && <p className="oshar-re-card__hint">Checking chain status...</p>}
                  {a.chain.minted && a.chain.owner && (
                    <p className="oshar-re-card__hint">{getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID)} sample token held by {shortenAddress(a.chain.owner)}</p>
                  )}
                  {cs?.status === "reserved" && a.reservationNote ? (
                    <p className="oshar-re-card__hint">{a.reservationNote}</p>
                  ) : null}
                  <p className="oshar-re-card__meta">
                    <span>{a.assetType || a.name}</span>
                    <span>{a.district || "Oshar District"}</span>
                  </p>
                  <p className="oshar-re-card__price">
                    <span>
                      {a.saleTierLabel || "Genesis Tier"} · {a.priceLabel}
                    </span>
                    <span className="oshar-re-card__supply">
                      Supply {(a.supply ?? 0).toLocaleString()}
                      {tierSource === "api" && a._remaining != null
                        ? ` · Remaining ${a._remaining.toLocaleString()}`
                        : tierSource === "loading"
                          ? " · Remaining — (loading live inventory)"
                        : tierSource === "fallback"
                          ? " · Remaining — (live registry offline)"
                          : ""}
                    </span>
                  </p>
                  {totalLine && <p className="oshar-re-card__hint">{totalLine}</p>}
                  <p className="oshar-re-card__hint" style={{ fontSize: "0.78rem", opacity: 0.9 }}>
                    Wallet mint creates one NFT per on-chain transaction unless the contract exposes a supported batch
                    mint (currently one mint per MetaMask confirmation).
                  </p>
                  {walletEthLine ? <p className="oshar-re-card__hint">{walletEthLine}</p> : null}
                  <p className="oshar-re-card__use">{a.shortDescription || a.useCase}</p>
                  {cs?.canShowQuantity ? (
                    <div className="oshar-re-card__qty" style={{ marginBottom: 10 }}>
                      <span className="oshar-re-card__hint">Quantity</span>
                      <button
                        type="button"
                        className="oshar-btn-outline oshar-re-btn-sm"
                        aria-label="Decrease quantity"
                        disabled={qty <= 1 || checkoutLoadingId === a.id || walletLoadingId === a.id}
                        onClick={() => bumpQty(a.id, -1, a._maxSel)}
                      >
                        −
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="oshar-re-qty-input"
                        value={qty}
                        disabled={checkoutLoadingId === a.id || walletLoadingId === a.id}
                        onChange={(e) => setQtyRaw(a.id, e.target.value, a._maxSel)}
                        aria-label="Quantity"
                      />
                      <button
                        type="button"
                        className="oshar-btn-outline oshar-re-btn-sm"
                        aria-label="Increase quantity"
                        disabled={qty >= a._maxSel || checkoutLoadingId === a.id || walletLoadingId === a.id}
                        onClick={() => bumpQty(a.id, 1, a._maxSel)}
                      >
                        +
                      </button>
                      <span className="oshar-re-card__hint">Max {a._maxSel} per checkout</span>
                    </div>
                  ) : null}

                  {cs?.canShowTerms ? (
                  <div
                    className="oshar-re-card__legal-compact"
                    role="region"
                    aria-label="Purchase terms"
                    style={
                      legalHighlightById[a.id]
                        ? {
                            outline: "2px solid rgba(201,165,74,0.65)",
                            outlineOffset: 2,
                            borderRadius: 8,
                          }
                        : undefined
                    }
                  >
                    <p className="oshar-re-card__legal-compact-title">Purchase terms</p>
                    <label className="oshar-re-card__legal-row">
                      <input
                        type="checkbox"
                        checked={Boolean(legal.termsOk)}
                        onChange={(e) => setLegalField(a.id, "termsOk", e.target.checked)}
                      />
                      <span>
                        I agree to Treydora&apos;s{" "}
                        <Link to={LEGAL_PATHS.terms} target="_blank" rel="noreferrer">
                          Terms of Use
                        </Link>
                        ,{" "}
                        <Link to={LEGAL_PATHS.digitalAssetRisk} target="_blank" rel="noreferrer">
                          Digital Asset Risk Disclosure
                        </Link>
                        ,{" "}
                        <Link to={LEGAL_PATHS.refundPolicy} target="_blank" rel="noreferrer">
                          Refund Policy
                        </Link>
                        , and the applicable{" "}
                        <Link to={LEGAL_PATHS.vrRealEstateTerms} target="_blank" rel="noreferrer">
                          digital access/license terms for this purchase
                        </Link>
                        .
                      </span>
                    </label>
                    <label className="oshar-re-card__legal-row">
                      <input
                        type="checkbox"
                        checked={Boolean(legal.vrOk)}
                        onChange={(e) => setLegalField(a.id, "vrOk", e.target.checked)}
                      />
                      <span>
                        I understand this purchase grants digital/virtual access rights inside the Treydora/Oshar
                        ecosystem only and does not transfer real-world real estate, land, securities, equity, ownership
                        in a company, or guaranteed income.
                      </span>
                    </label>
                  </div>
                  ) : null}

                  {(cs?.canUseWallet || cs?.canUseCard) && (
                  <p className="oshar-re-card__hint" style={{ marginTop: 8, marginBottom: 0 }} role="note">
                    Card purchases reserve digital access in your Treydora account. Wallet minting, where available,
                    creates an on-chain record after confirmation.
                  </p>
                  )}

                  {(cs?.canUseWallet || cs?.canUseCard) && (
                  <div className="oshar-re-card__actions">
                    <button
                      type="button"
                      className={`${
                        walletPrimaryVisual ? "oshar-btn-gold" : "oshar-btn-outline"
                      } oshar-re-btn-sm oshar-re-wallet-cta${
                        !cs?.canUseWallet ? " oshar-re-wallet-cta--soft-block" : ""
                      }${walletHardBusy ? " oshar-re-wallet-cta--hard-busy" : ""}`}
                      disabled={walletHardBusy}
                      aria-busy={isWalletActionInFlight ? true : undefined}
                      onClick={(e) => handleWalletButtonClick(e, a, qty, walletStep)}
                    >
                      {walletLabel}
                    </button>
                    {cs?.canUseCard ? (
                    <button
                      type="button"
                      className="oshar-btn-outline oshar-re-btn-sm"
                      disabled={reserveDisabled}
                      onClick={() => handlePayWithCard(a, qty)}
                    >
                      {cardButtonLabel}
                    </button>
                    ) : null}
                  </div>
                  )}

                  {walletPhaseById[a.id]?.stage === "done" ? (
                    <p className="oshar-re-card__pay-status" role="status">
                      <Link
                        to="/dashboard#oshar-access"
                        style={{ color: "#d4b96a", textDecoration: "underline" }}
                      >
                        Mint complete — View Dashboard
                      </Link>
                    </p>
                  ) : null}

                  {payMsg ? (
                    <p className="oshar-re-card__pay-status" role="status">
                      {payMsg}
                    </p>
                  ) : null}
                  {walletMsg ? (
                    <p className="oshar-re-card__pay-status" role="status" style={{ whiteSpace: "pre-wrap" }}>
                      {walletMsg}
                    </p>
                  ) : null}
                  {walletDashboardSyncPendingById[a.id] ? (
                    <div className="oshar-re-card__pay-status" style={{ marginTop: 6 }}>
                      <button
                        type="button"
                        className="oshar-btn-outline oshar-re-btn-sm"
                        disabled={walletLoadingId === a.id}
                        onClick={() => handleRetryOsharDashboardSync(a.id)}
                      >
                        {walletLoadingId === a.id ? "Retrying…" : "Retry Dashboard Sync"}
                      </button>
                    </div>
                  ) : null}

                  {showWalletDiagnostics && walletMintDiagById[a.id] ? (
                    <details className="oshar-re-card__diag-details">
                      <summary>Wallet mint diagnostic (developer)</summary>
                      <pre className="oshar-re-card__diag-body" role="region" aria-label="Wallet mint diagnostic">
                        {(() => {
                          const wd = walletMintDiagById[a.id];
                          const lines = [
                            `clickFired: ${wd.clickFired || "—"}`,
                            `signedInTokenPresent: ${wd.signedInTokenPresent || "—"}`,
                            `apiBaseUrl: ${wd.apiBaseUrl || "—"}`,
                            `selectedTierId: ${wd.selectedTierId || "—"}`,
                            `selectedTierSlug: ${wd.selectedTierSlug || "—"}`,
                            `quantity: ${wd.quantity || "—"}`,
                            `prepareAttempted: ${wd.prepareAttempted || "—"}`,
                            `prepareUrl: ${wd.prepareUrl || "—"}`,
                            `prepareStatus: ${wd.prepareStatus || "—"}`,
                            `prepareErrorName: ${wd.prepareErrorName || "—"}`,
                            `prepareErrorMessage: ${wd.prepareErrorMessage || "—"}`,
                            `prepareResponseMessage: ${wd.prepareResponseMessage || "—"}`,
                            `metamaskDetected: ${wd.metamaskDetected || "—"}`,
                            `metamaskOpenAttempted: ${wd.metamaskOpenAttempted || "—"}`,
                            `chainId: ${wd.chainId || "—"}`,
                            `contractAddress: ${wd.contractAddress || "—"}`,
                            `syncAttempted: ${wd.syncAttempted || "—"}`,
                            `syncStatus: ${wd.syncStatus || "—"}`,
                          ];
                          return lines.join("\n");
                        })()}
                      </pre>
                    </details>
                  ) : null}

                  {debugPurchase && (
                    <details className="oshar-re-card__diag-details">
                      <summary>Purchase debug (developer)</summary>
                      <pre className="oshar-re-card__diag-body oshar-re-card__debug">
                      {[
                        `component: PropertyTypeGrid`,
                        `API base URL: ${String(getOsharApiBaseUrl() || "").trim() || "(empty)"}`,
                        `GET /api/oshar-real-estate/tiers http: ${tiersHttpMeta?.httpStatus ?? "—"}`,
                        `GET /tiers ok: ${tiersHttpMeta?.ok ? "yes" : "no"}`,
                        `GET /tiers errorKey: ${tiersHttpMeta?.errorKey ?? "—"}`,
                        `using backend tier cards: ${tierSource === "api" ? "yes" : "no"}`,
                        `using fallback tier cards: ${tierSource === "fallback" ? "yes" : "no"}`,
                        `registry / tiers hint: ${registryOfflineHint}`,
                        `prepare request URL: ${walletMintPrepareUrl || "(empty base)"}`,
                        `tierSlug: ${slug || "(empty)"}`,
                        `quantity: ${qty}`,
                        `legal accepted: ${legalComplete ? "yes" : "no"}`,
                        `auth token present: ${tokenPresent ? "yes" : "no"}`,
                        `wallet button label: ${walletLabel}`,
                        `wallet button disabled (HTML): ${walletHardBusy ? "yes" : "no"}`,
                        `wallet aria-disabled: ${walletHardBusy ? "true" : "false"}`,
                        `wallet oshar-re-wallet-cta--hard-busy: ${walletHardBusy ? "yes" : "no"}`,
                        `wallet pointer-events (CSS): ${walletHardBusy ? "none (hard-busy)" : "auto"}`,
                        `wallet cursor (CSS): ${walletHardBusy ? "progress (hard-busy)" : "pointer on enabled"}`,
                        `wallet oshar-re-wallet-cta--soft-block: ${!cs?.canUseWallet ? "yes" : "no"}`,
                        `wallet gate / busy reason: ${cs?.disabledReason || "(none — CTA clickable)"}`,
                        `isWalletActionInFlight: ${isWalletActionInFlight ? "yes" : "no"}`,
                        `has window.ethereum: ${hasInjectedWallet ? "yes" : "no"}`,
                        `connected account: ${address ? shortenAddress(address) : "(none)"}`,
                        `current chain id: ${chain?.id != null ? String(chain.id) : "(unknown)"}`,
                        `wallet phase: ${JSON.stringify(walletPhaseById[a.id] || null)}`,
                        `lastWalletClickReached: ${wDbg.lastWalletClickReached ? "yes" : "no"}`,
                        `walletStatus: ${wDbg.walletStatus || "—"}`,
                        `lastWalletAction: ${wDbg.lastWalletAction || "—"}`,
                        `lastWalletError: ${wDbg.lastWalletError || "—"}`,
                        `prepareRequestFired: ${wDbg.prepareRequestFired ? "yes" : "no"}`,
                        `prepareOk: ${wDbg.prepareOk ? "yes" : "no"}`,
                        `prepareBlockers: ${wDbg.prepareBlockers || "—"}`,
                        `prepareTokenIds: ${wDbg.prepareTokenIds || "—"}`,
                        `prepareEthSample: ${wDbg.prepareEthSample || "—"}`,
                        `prepareError: ${wDbg.prepareError || "—"}`,
                        `remaining: ${a._remaining != null ? a._remaining : "n/a (fallback)"}`,
                        `maxPerCheckout: ${a.maxPerCheckout ?? "derived"}`,
                        `card state status: ${cs?.status || "—"}`,
                        `canUseWallet: ${cs?.canUseWallet ? "yes" : "no"}`,
                        `canUseCard: ${cs?.canUseCard ? "yes" : "no"}`,
                        `auth context: ${isAuthenticated ? "yes" : "no"}`,
                        `pay button disabled (HTML): ${reserveDisabled ? "yes" : "no"}`,
                        `tier gate: ${a._checkoutDisabledReason || "ok"}`,
                        `last clicked action: ${debugSnapById[a.id]?.lastAction || "—"}`,
                        `request fired: ${debugSnapById[a.id]?.requestFired ? "yes" : "no"}`,
                        `last response: ${debugSnapById[a.id]?.lastResponseStatus ?? "—"}`,
                      ].join("\n")}
                      </pre>
                    </details>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PropertyTypeGrid;
